"use client";

import { useRef, useState, useEffect, useCallback } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, opts: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export function TurnstileForm({
  siteKey,
  action,
  method = "POST",
  className,
  children,
  successTitle = "Thank you!",
  successMessage = "Your response has been received. We appreciate you taking the time to reach out.",
  trackingType,
}: {
  siteKey: string;
  action: string;
  method?: string;
  className?: string;
  children: React.ReactNode;
  successTitle?: string;
  successMessage?: string;
  trackingType?: "support" | "contact" | "subscribe";
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  // Render Turnstile widget eagerly on mount
  useEffect(() => {
    const renderWidget = () => {
      if (widgetIdRef.current || !turnstileRef.current || !window.turnstile) return;
      const key = siteKey || "0x4AAAAAAB7-7KQO4wfPGwUh";
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: key,
        callback: (token: string) => {
          tokenRef.current = token;
        },
        "expired-callback": () => {
          tokenRef.current = null;
        },
        "error-callback": () => {
          tokenRef.current = null;
        },
        theme: "light",
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [siteKey]);

  const getToken = useCallback((): Promise<string | null> => {
    if (tokenRef.current) return Promise.resolve(tokenRef.current);

    return new Promise((resolve) => {
      let elapsed = 0;
      const poll = setInterval(() => {
        if (tokenRef.current) {
          clearInterval(poll);
          resolve(tokenRef.current);
        }
        elapsed += 200;
        // 20s allows iOS PAT 401 → interactive-challenge fallback to complete
        if (elapsed > 20000) {
          clearInterval(poll);
          resolve(null);
        }
      }, 200);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form || state === "submitting") return;

    setState("submitting");
    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.dataset.originalText = btn.textContent || "";
      btn.textContent = "Submitting...";
    }

    try {
      const token = await getToken();
      if (!token) {
        setErrorMsg("We couldn't verify you're human. Please complete the verification challenge below, or disable any ad blockers and try again.");
        setState("error");
        return;
      }
      const formData = new FormData(form);
      formData.set("cf-turnstile-response", token);

      const res = await fetch(action, {
        method,
        body: formData,
        redirect: "follow",
      });

      const url = res.url || "";
      if (url.includes("submitted=1") || url.includes("subscribed=1")) {
        setState("success");
      } else if (url.includes("error=")) {
        const errParam = new URL(url).searchParams.get("error");
        setErrorMsg(errParam || "Something went wrong. Please try again.");
        setState("error");
      } else {
        setErrorMsg("Something went wrong. Please try again.");
        setState("error");
      }
    } catch (err: any) {
      console.error("Submission failed:", err);
      const msg = err?.message || "Something went wrong. Please try again.";
            setErrorMsg(msg);
      setState("error");
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = btn.dataset.originalText || "Submit";
      }
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        tokenRef.current = null;
      }
    }
  };

  if (state === "success") {
    return (
      <div className="text-center py-10">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-grass-green/10 mb-5">
          <svg className="w-7 h-7 text-grass-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-display text-2xl text-bunting-navy mb-2">{successTitle}</h3>
        <p className="text-bunting-navy/60 text-sm max-w-md mx-auto">{successMessage}</p>
        <p className="text-xs text-bunting-navy/30 mt-6 max-w-md mx-auto">
          Didn't receive a confirmation email? Please allow up to 1 hour, then contact{" "}
          <a href="mailto:info@july4thpocketparade.com" className="text-summer-sky hover:underline">info@july4thpocketparade.com</a>
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} method={method} action={action} className={className} onSubmit={handleSubmit}>
      {children}

      {state === "error" && (
        <div className="bg-red-50 border border-red-200 rounded px-4 py-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      <div ref={turnstileRef} className="mt-2" />
    </form>
  );
}
